import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "@/config/api";

const API = API_BASE;

// Axios defaults
axios.defaults.withCredentials = true;

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
});

const TOKEN_KEY = "asistentpersonal-token";

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  axios.defaults.headers.common["Authorization"] = token ? `Bearer ${token}` : "";
}

// Initialize header from storage on module load
setStoredToken(getStoredToken());

function formatApiError(detail, err) {
  const status = err?.response?.status
  const ctype = typeof err?.response?.headers?.["content-type"] === "string" ? err.response.headers["content-type"] : ""
  const payload = err?.response?.data

  if (!err?.response && err?.request) {
    return "Network error. Check your connection and try again."
  }
  if (status === 400 && typeof payload === "string" && payload.includes("InvalidRequest")) {
    return "API route not found. Redeploy with /api ingress or set REACT_APP_BACKEND_URL.";
  }
  if (status === 503) {
    return typeof detail === "string"
      ? detail
      : "Server is starting or the database may still be connecting. Try again in a minute.";
  }

  const bodyLooksHtml =
    ctype.includes("text/html") || (typeof payload === "string" && /^\s*<!DOCTYPE\b/i.test(payload))

  if (detail == null) {
    if (status === 401) return "Invalid email or password."
    if (bodyLooksHtml && status >= 502 && status <= 504) {
      return "Cannot reach the API (gateway timed out). Check App Platform runtime logs and that DATABASE_URL uses the Supabase session pooler and a URL-encoded password."
    }
    if (status === 504) return "Request timed out. Try again shortly or check backend health."
    if (status >= 500 && status !== 503) return `Server error (${status}). Check App Platform runtime logs if it continues.`
    return "Something went wrong. Please try again."
  }
  if (typeof detail === "string") return detail
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ")
  if (detail && typeof detail.msg === "string") return detail.msg
  return String(detail)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, totpCode) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, totp_code: totpCode || undefined });
    if (data.requires_totp) return data;
    if (data.access_token) setStoredToken(data.access_token);
    // Login payload omits entitlements — load /auth/me so FeatureGate/sidebar unlock.
    const { data: me } = await axios.get(`${API}/auth/me`);
    setUser(me);
    return me;
  };

  const acceptInvite = async (token, payload) => {
    const { data } = await axios.post(`${API}/auth/invite/${token}/accept`, payload);
    if (data.access_token) setStoredToken(data.access_token);
    const { data: me } = await axios.get(`${API}/auth/me`);
    setUser(me);
    return me;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    if (data.access_token) setStoredToken(data.access_token);
    const { data: me } = await axios.get(`${API}/auth/me`);
    setUser(me);
    return me;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setStoredToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, acceptInvite, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
